import {Injectable, Optional} from '@angular/core';
import {BehaviorSubject, map, Observable} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {Configurations} from 'projects/eye-tracker/src/lib/configurations';
import {DeviceDetectorService} from 'ngx-device-detector';

declare var gazefilter;
// const WASM_URL = `./assets/gazefilter.wasm?${new Date().getTime()}`;

const WASM_URL = `https://cdn.jsdelivr.net/npm/gazefilter/dist/gazefilter.wasm`;

interface DocumentWithFullscreen extends HTMLDocument {
    mozFullScreenElement?: Element;
    msFullscreenElement?: Element;
    webkitFullscreenElement?: Element;
    msExitFullscreen?: () => void;
    mozCancelFullScreen?: () => void;
    webkitExitFullscreen?: () => void;
}

@Injectable({
    providedIn: 'any',
})
export class EyeTrackerService {
    public apiUrl = 'No value';

    _wasmConfigLoaded = 0;
    _wasmConfigTotal = 0;

    fullScreenChange: BehaviorSubject<any> = new BehaviorSubject(false);
    _gazefilterInited: BehaviorSubject<any> = new BehaviorSubject(null);
    private _gazefilter: any;
    private _filter: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    constructor(
        private http: HttpClient,
        private deviceService: DeviceDetectorService,
        @Optional() config?: Configurations,
    ) {
        this.filter = this.filter.bind(this);
        if (config) {
            this.apiUrl = config.apiUrl;
        }
    }

    async fetchConfig() {
        const response: any = await fetch(WASM_URL);
        const reader = response.body.getReader();
        const contentLength = +response.headers.get('Content-Length');

        let receivedLength = 0; // received that many bytes at the moment
        const chunks = []; // array of received binary chunks (comprises the body)
        while (true) {
            const {done, value} = await reader.read();

            if (done) {
                break;
            }

            chunks.push(value);
            receivedLength += value.length;
            this.wasmConfigLoaded = receivedLength;
            this.wasmConfigTotal = contentLength;
        }

        const chunksAll = new Uint8Array(receivedLength);
        let position = 0;
        for (const chunk of chunks) {
            chunksAll.set(chunk, position);
            position += chunk.length;
        }
        return chunksAll;
    }

    getApiUrl() {
        return this.apiUrl;
    }

    get wasmConfigLoaded() {
        return this._wasmConfigLoaded;
    }

    set wasmConfigLoaded(prop) {
        this._wasmConfigLoaded = prop;
    }

    get wasmConfigTotal() {
        return this._wasmConfigTotal;
    }

    set wasmConfigTotal(prop) {
        this._wasmConfigTotal = prop;
    }

    get gazefilter() {
        return this._gazefilter;
    }

    set gazefilter(prop: any) {
        this._gazefilter = prop;
    }

    get gazefilterInited() {
        return this._gazefilterInited;
    }

    set gazefilterInited(prop: any) {
        this._gazefilterInited.next(prop);
    }

    gazefilterInitedAsPromise(): Promise<any> {
        return new Promise((resolve) => {
            this.gazefilterInited.subscribe((prop) => {
                if (prop === true) {
                    resolve(true);
                } else if (prop === false) {
                    resolve(false);
                }
            });
        });
    }

    generateImage(el: any): Observable<any> {
        return this.http.get(`${this.apiUrl}/storage/content/${el}`, {responseType: 'blob'})
            .pipe(
                map(() => {
                    return {
                        id: el,
                        src: `url(${this.apiUrl}/storage/content/${el})`
                    };
                }),
            );
    }

    filter(prop?: any): Observable<any> {
        if (typeof prop !== 'undefined') {
            this._filter.next(prop);
        }

        return this._filter.asObservable();
    }

    checkBrowser(): Promise<any> {
        return new Promise((resolve, reject) => {
            const isMobile = this.deviceService.isMobile();
            const isTablet = this.deviceService.isTablet();
            if (isMobile || isTablet) {
                reject('chromeNotConfirmed');
            } else {
                resolve(true);
            }
            resolve(true);

        });
    }

    async checkWebCamAllowed(): Promise<boolean> {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({video: true});
            stream.getTracks().forEach(track => track.stop());
            return true;
        } catch (error) {
            throw new Error('webCamNotAllowed');
        }
    }

    async initGaz(config): Promise<any> {
        return gazefilter?.init(config)
            .then(() => {
                this.gazefilter = gazefilter;
                return gazefilter;
            })
            .catch((err) => {
                throw err;
            });

    }

    addFilter(gf: any) {
        return gf.tracker.addListener('filter', (event: any) => {
            this.filter(event.detected);
        });
    }

    async connect(gf: any): Promise<any> {
        return gf.tracker.connect();
    }

    async initCanvas(gf: any, canvas): Promise<any> {
        return gf.visualizer.setCanvas(canvas);
    }

    initRender(gf: any) {
        gf.visualizer.setListener('frame', (ctx: CanvasRenderingContext2D, trackEvent: any) => {
            ctx.drawImage(
                gazefilter.tracker.videoElement(),
                0, 0, ctx.canvas.width, ctx.canvas.height
            );

            // set drawing style
            ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
            ctx.strokeStyle = 'white';
            ctx.fillStyle = 'white';
            ctx.lineWidth = 4;

            // draw facial landmarks
            if (trackEvent.detected) {
                // const shapeArray = trackEvent.shapeArrayView();
                // for (let i = 0; i < trackEvent.shapeSize(); i++) {
                //     ctx.beginPath();
                //     const pointX = shapeArray[i * 2];
                //     const pointY = shapeArray[i * 2 + 1];
                //     ctx.arc(pointX, pointY, 2, 0, Math.PI * 2);
                //     ctx.fill();
                // }
                // draw pupil center points
                ctx.strokeStyle = '#10082F';
                const [lx, ly, rx, ry] = trackEvent.pupilArray();
                if (!isNaN(lx)) {
                    ctx.beginPath();
                    ctx.arc(lx, ly, 12, 0, Math.PI * 2);
                    ctx.stroke();
                }

                if (!isNaN(rx)) {
                    ctx.beginPath();
                    ctx.arc(rx, ry, 12, 0, Math.PI * 2);
                    ctx.stroke();
                }
            }

        });
    }

    fullScreen(prop: boolean): Promise<any> {
        if (prop) {
            return this.openfullscreen();
        } else {
            return this.closefullscreen()
                .then(() => {
                    this.fullScreenEvent(false);
                });
        }
    }

    openfullscreen() {
        // Trigger fullscreen
        const el = document.getElementById('main') as any;
        if (el.requestFullscreen) {
            return el.requestFullscreen();
        } else {
            if (el.mozRequestFullScreen) { /* Firefox */
                el.mozRequestFullScreen();
            } else if (el.webkitRequestFullscreen) { /* Chrome, Safari and Opera */
                el.webkitRequestFullscreen();
            } else if (el.webkitRequestFullScreen) { /* Chrome, Safari and Opera */
                el.webkitRequestFullScreen();
            } else if (el.msRequestFullscreen) { /* IE/Edge */
                el.msRequestFullscreen();
            }
            return new Promise((resolve) => {
                resolve(true);
            });
        }

    }

    closefullscreen() {
        const doc = document as DocumentWithFullscreen;
        if (doc.fullscreenElement) {
            return doc.exitFullscreen();
        } else {
            if (doc.mozFullScreenElement) { /* Firefox */
                doc.mozCancelFullScreen();
            } else if (doc.webkitFullscreenElement) { /* Chrome, Safari and Opera */
                doc.webkitExitFullscreen();
            } else if (doc.msFullscreenElement) { /* IE/Edge */
                doc.msExitFullscreen();
            }
            return new Promise((resolve) => {
                resolve(true);
            });
        }
    }

    fullScreenEvent(prop: boolean) {
        this.fullScreenChange.next(prop);
    }

    get isFullScreen() {
        return document.fullscreenElement;
    }

    async waitUntil(time: number) {
        return await new Promise(resolve => {
            const interval = setInterval(() => {
                resolve(true);
                clearInterval(interval);
            }, time);
        });
    }

    set addCalibrationOnTest(prop: any) {
        localStorage.setItem('addCalibrationOnTest', prop);
    }

    get addCalibrationOnTest() {
        return JSON.parse(localStorage.getItem('addCalibrationOnTest')) || false;
    }

}

